import { useDispatch } from "react-redux";
import {
  setSignUpLoading,
  setSignUpSuccess,
  setSignUpFailure,
  setSignInLoading,
  setSignInSuccess,
  setSignInFailure,
  setVerifyTokenPasswordLoading,
  setVerifyTokenPasswordSuccess,
  setVerifyTokenPasswordFailure,
  setForgotPasswordLoading,
  setForgotPasswordSuccess,
  setForgotPasswordFailure,
  setResetPasswordLoading,
  setResetPasswordSuccess,
  setResetPasswordFailure,
  setLogout,
  setLogoutLoading,
  setLogoutSuccess,
  setLogoutFailure,
} from "../redux/slices/authenticationSlice";
import { signup } from "../services/authentication/signup";
import { signin } from "@services/authentication/signin";
import { forgotPassword } from "../services/authentication/forgotPassword";
import { verifyTokenPassword } from "../services/authentication/verifyTokenPassword";
import { resetPassword } from "../services/authentication/resetPassword";
import { useNavigate } from "react-router";
import { notify } from "src/utils/notify";
import getErrorStatement from "src/utils/getErrorStatement";
import { clearToken } from "src/utils/indexedDb";
import useIndexedDB from "./useIndexedDB";
import { logout } from "../services/authentication/logout";

const useAuth = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { storeToken } = useIndexedDB();

  const handleSignUp = async (data) => {
    try {
      dispatch(setSignUpLoading());
      const response = await signup(data);
      dispatch(setSignUpSuccess(response.data));
      storeToken(response.data.authToken);
      navigate("/");
      notify("Account created successfully", "success");
    } catch (error) {
      dispatch(setSignUpFailure(error.message));
      notify(getErrorStatement(error));
    }
  };

  const handleSignIn = async (data) => {
    try {
      dispatch(setSignInLoading());
      const response = await signin(data);
      dispatch(setSignInSuccess(response.data));
      storeToken(response.data.authToken);
      navigate("/");
    } catch (error) {
      dispatch(setSignInFailure(error.message));
      console.error(error);
      notify(getErrorStatement(error));
    }
  };

  const handleForgotPassword = async (data, setShowVerifyOtpUi) => {
    try {
      dispatch(setForgotPasswordLoading());
      const response = await forgotPassword(data);
      dispatch(setForgotPasswordSuccess(response.data));
      setShowVerifyOtpUi(true);
    } catch (error) {
      dispatch(setForgotPasswordFailure(error.message));
      notify(getErrorStatement(error));
    }
  };

  const handleVerifyTokenPassword = async (data, setShowResetPassoword) => {
    try {
      dispatch(setVerifyTokenPasswordLoading());
      const response = await verifyTokenPassword(data);
      if (setShowResetPassoword) {
        setShowResetPassoword(true);
      }
      dispatch(setVerifyTokenPasswordSuccess(response.data));
    } catch (error) {
      dispatch(setVerifyTokenPasswordFailure(error.message));
      notify(getErrorStatement(error));
    }
  };

  const handleResetPassword = async (data) => {
    try {
      dispatch(setResetPasswordLoading());
      const response = await resetPassword(data);
      dispatch(setResetPasswordSuccess(response.data));
      navigate("/signin");
      notify("Password has been reset successfully", "success");
    } catch (error) {
      dispatch(setResetPasswordFailure(error.message));
      notify(getErrorStatement(error));
    }
  };

  const handleLogoutSession = async () => {
    try {
      dispatch(setLogoutLoading());
      await logout();
      clearToken();
      dispatch(setLogout());
      dispatch(setLogoutSuccess());
      navigate("/signin");
    } catch (error) {
      dispatch(setLogoutFailure(error.message));
      notify(getErrorStatement(error));
    }
  };

  return {
    handleSignUp,
    handleSignIn,
    handleForgotPassword,
    handleVerifyTokenPassword,
    handleResetPassword,
    handleLogoutSession,
  };
};

export default useAuth;
